import dayjs from 'dayjs';

// Converts an array of range numbers to a format with min and max values.
// Example: [{ min: 1, max: 5 }, { min: 3, max: 9 }] becomes [{ "<>": [1, 5] }, { "<>": [3, 9] }]
function convertRangeNumbers(data) {
  return data.map(({ min, max }) => ({
    "<>": [Math.min(min, max), Math.max(min, max)]
  }));
}

// Converts an array of date ranges to formatted strings for the min and max dates.
// Example: [{ start: '2023-01-01', end: '2023-12-31' }] becomes [{ "<>": ["2023-01-01", "2023-12-31"] }]
function convertDateRanges(data) {
  return data.map(({ start, end }) => ({
    "<>": [
      dayjs.min(dayjs(start), dayjs(end)).format("YYYY-MM-DD"),
      dayjs.max(dayjs(start), dayjs(end)).format("YYYY-MM-DD")
    ]
  }));
}

// Processes values based on data type and range status.
// Returns an array of formatted data or null if data is invalid.
// Example: processValue([{ min: 1, max: 5 }], 'number', true) returns [{ "<>": [1, 5] }]
function processValue(value, dataType, isRange) {
  if (!Array.isArray(value) || value.length === 0) return null;

  switch (dataType) {
    case "number":
      return isRange ? convertRangeNumbers(value) : value;
    case "date":
      return isRange ? convertDateRanges(value) : value.map(v => dayjs(v).format("YYYY-MM-DD"));
    case "string":
      return value;
    default:
      return null;
  }
}

// Main function to generate filter objects based on input data.
// Returns an object with filters for each key in inputData if valid.
export function createFilters(inputData) {
  if (!inputData || typeof inputData !== "object" || Object.keys(inputData).length === 0) return {};

  const filters = {};

  // Iterate over each key in inputData
  Object.keys(inputData).forEach((key) => {
    const { isRange, dataType, value } = inputData[key] || {};
    const processedData = processValue(value, dataType, isRange);

    // Add the processed data to filters if it's valid
    if (processedData !== null) {
      filters[key] = { data: processedData, type: dataType };
    }
  });

  return filters;
}

/*
Sample Input Example:
const inputData = {
  age: {
    isRange: true,
    dataType: "number",
    value: [{ min: 20, max: 30 }, { min: 25, max: 35 }]
  },
  birthDate: {
    isRange: true,
    dataType: "date",
    value: [{ start: "2000-01-01", end: "2005-12-31" }]
  },
  name: {
    isRange: false,
    dataType: "string",
    value: ["Alice", "Bob"]
  },
  height: {
    isRange: false,
    dataType: "number",
    value: 180
  }
};

Sample Output:
{
  age: {
    data: [{ "<>": [20, 30] }, { "<>": [25, 35] }],
    type: "number"
  },
  birthDate: {
    data: [{ "<>": ["2000-01-01", "2005-12-31"] }],
    type: "date"
  },
  name: {
    data: ["Alice", "Bob"],
    type: "string"
  },
  height: {
    data: 180,
    type: "number"
  }
}
*/
